"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Panel,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import {
  buildSmartGoal,
  CHECK_INS,
  type SmartGoalInput,
} from "@/lib/tool-kit/essay-templates/smart-goal";

/** English strings. A translated content file overrides these through `ui`. */
export const SMART_GOAL_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  goalLabel: "Your goal, in rough form",
  goalPlaceholder: "e.g. Do better in statistics",
  criteriaHeading: "Make it SMART",
  specificLabel: "Specific — I will…",
  specificPlaceholder: "e.g. complete two practice problem sets a week",
  specificHint: "What exactly will you do? Start with a verb.",
  measurableLabel: "Measurable — measured by…",
  measurablePlaceholder: "e.g. my quiz scores, rising from 65% to 80%",
  measurableHint: "A number you can track: a score, a count, hours, or pages.",
  achievableLabel: "Achievable — this is realistic because…",
  achievablePlaceholder:
    "e.g. I have four free hours a week and the answer key",
  achievableHint: "The time, help, and resources you actually have.",
  relevantLabel: "Relevant — this matters because…",
  relevantPlaceholder: "e.g. I need a B in the course to stay in my major",
  relevantHint: "The bigger aim this goal serves.",
  timeHeading: "Time-bound",
  deadlineLabel: "Deadline",
  checkInLabel: "Check your progress",
  checkIns: {
    weekly: "Every week",
    biweekly: "Every two weeks",
    monthly: "Every month",
  },
  stepsLabel: "First steps — one per line (optional)",
  stepsPlaceholder: "e.g. Block two hours on Tuesday evenings",
  fileName: "smart-goal",
  document: {
    heading: "Your SMART goal",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Your goal statement and action plan are ready.",
  },
  defaultTitle: "SMART Goal",
  startingPoint: "Starting point: {goal}",
  headings: {
    statement: "Goal Statement",
    checklist: "SMART Checklist",
    plan: "Action Plan",
  },
  statement: {
    by: "By {date},",
    will: "I will {action}",
    measured: "measured by {measure}",
    because: "because {reason}",
  },
  statementPrompt:
    "Fill in at least the Specific part above to build your goal statement.",
  statementNote:
    "Read it aloud. If someone else could not tell whether you had achieved it, make it more specific or more measurable — and check that each part reads naturally where it joins the sentence.",
  criteria: {
    specific: "Specific",
    measurable: "Measurable",
    achievable: "Achievable",
    relevant: "Relevant",
    timeBound: "Time-bound",
  },
  missing: {
    specific: "say exactly what you will do, starting with a verb.",
    measurable: "add a number you can track — a score, a count, a total.",
    achievable: "check the time and resources you really have.",
    relevant:
      "connect the goal to a bigger aim: a course, a grade, a career step.",
    timeBound: "set a deadline.",
  },
  stepsPrompt:
    "Break the goal into three to five steps, and make the first one something you can do this week.",
  checkIn:
    "Review your progress {frequency}, and adjust the plan if you fall behind.",
  frequencies: {
    weekly: "every week",
    biweekly: "every two weeks",
    monthly: "once a month",
  },
  example: {
    goal: "Do better in organic chemistry",
    specific:
      "complete two past-paper problem sets each week and review every mistake against the worked answers",
    measurable:
      "my scores on four timed practice exams, rising from 62% to at least 75%",
    achievable:
      "I have six free hours a week on Tuesdays and Thursdays, and the department posts past papers with worked answers",
    relevant:
      "I need at least a B in Organic Chemistry I to stay on the pre-med track",
    steps:
      "Download the last four years of past papers this weekend\nBlock three hours on Tuesday and Thursday evenings in my calendar\nKeep an error log of every problem I get wrong\nTake a timed practice exam every two weeks and record the score\nBook office hours if my score has not improved after two practice exams",
  },
} as const;

const EMPTY: SmartGoalInput = {
  goal: "",
  specific: "",
  measurable: "",
  achievable: "",
  relevant: "",
  deadline: "",
  checkIn: "weekly",
  steps: "",
};

/** The example's deadline: ten weeks from the day the button is pressed. */
const EXAMPLE_WEEKS = 10;

function weeksFromToday(weeks: number): string {
  const date = new Date();
  date.setDate(date.getDate() + weeks * 7);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

type TextKey = Exclude<keyof SmartGoalInput, "checkIn">;

const CRITERIA_FIELDS = [
  {
    key: "specific",
    label: "specificLabel",
    placeholder: "specificPlaceholder",
    hint: "specificHint",
  },
  {
    key: "measurable",
    label: "measurableLabel",
    placeholder: "measurablePlaceholder",
    hint: "measurableHint",
  },
  {
    key: "achievable",
    label: "achievableLabel",
    placeholder: "achievablePlaceholder",
    hint: "achievableHint",
  },
  {
    key: "relevant",
    label: "relevantLabel",
    placeholder: "relevantPlaceholder",
    hint: "relevantHint",
  },
] as const;

export default function SmartGoalGeneratorWidget() {
  const strings = useToolUi(SMART_GOAL_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const [input, setInput] = useState<SmartGoalInput>(EMPTY);
  const doc = useMemo(
    () => buildSmartGoal(input, strings, locale),
    [input, strings, locale]
  );
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setInput({
              ...EMPTY,
              ...strings.example,
              deadline: weeksFromToday(EXAMPLE_WEEKS),
              checkIn: "weekly",
            })
          }
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel>
        <TextField
          id="sg-goal"
          label={strings.goalLabel}
          onChange={update("goal")}
          placeholder={strings.goalPlaceholder}
          value={input.goal}
        />
      </Panel>

      <Panel title={strings.criteriaHeading}>
        {CRITERIA_FIELDS.map((field) => (
          <TextAreaField
            hint={strings[field.hint]}
            id={`sg-${field.key}`}
            key={field.key}
            label={strings[field.label]}
            onChange={update(field.key)}
            placeholder={strings[field.placeholder]}
            prose
            rows={2}
            value={input[field.key]}
          />
        ))}
      </Panel>

      <Panel title={strings.timeHeading}>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="sg-deadline"
            label={strings.deadlineLabel}
            onChange={update("deadline")}
            type="date"
            value={input.deadline}
          />
          <SelectField
            id="sg-check-in"
            label={strings.checkInLabel}
            onChange={(checkIn) =>
              setInput((previous) => ({ ...previous, checkIn }))
            }
            options={CHECK_INS.map((value) => ({
              value,
              label: strings.checkIns[value],
            }))}
            value={input.checkIn}
          />
        </div>
        <TextAreaField
          id="sg-steps"
          label={strings.stepsLabel}
          onChange={update("steps")}
          placeholder={strings.stepsPlaceholder}
          prose
          rows={4}
          value={input.steps}
        />
      </Panel>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="sg"
        labels={strings.document}
      />
    </div>
  );
}
